import React, { useContext, useState } from "react";
import { Plus as PlusIcon } from "@actinc/dls/icons";
import _ from "lodash/fp";
import { Box, Button, DialogContentText } from "@mui/material";
import { PropTypes } from "prop-types";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  AlertDialog,
  SimpleTable,
  StandardsDialog,
} from "../../../../components";

import { useMemoCompare } from "../../../../services/hooks";
import {
  getTertiaryStandards,
  supportsAutomatedStandards,
} from "../../../../services/standardsCrosswalk";

import { StandardsContext } from "../../../../standardsProvider";

const StandardsTab = ({ objectType, readonly }) => {
  const standardsList = useContext(StandardsContext);

  const { control, getValues, watch } = useFormContext();
  const { fields, replace } = useFieldArray({
    control,
    name: `${objectType}.standards`,
  });
  watch(`${objectType}.standards`);

  const contentArea = watch(`${objectType}.contentArea`);

  const standards = useMemoCompare(
    fields.map(({ id, humanCodingScheme, order }) => ({
      fullStatement:
        standardsList.find((o) => o.humanCodingScheme === humanCodingScheme)
          ?.fullStatement || ``,
      humanCodingScheme,
      id,
      order: _.capitalize(order),
    })),
  );

  const [dialogProps, setDialogProps] = useState({
    open: false,
  });

  const [alertDialogProps, setAlertDialogProps] = useState({ open: false });

  const getStandards = () => getValues(`${objectType}.standards`);
  const findStandard = (humanCodingScheme) =>
    getStandards().find((std) => std.humanCodingScheme === humanCodingScheme);

  const showAlert = (message, onConfirm, title = "Automate Standards") => {
    setAlertDialogProps({
      confirmButtonText: onConfirm ? "Add Standards" : "OK",
      maxWidth: "sm",
      message,
      onClose: (confirmed) => {
        setAlertDialogProps({ open: false });
        if (confirmed && onConfirm) onConfirm();
      },
      open: true,
      dialogTitle: title,
      showCancelButton: !!onConfirm,
    });
  };

  const handleAutomateStandards = async () => {
    try {
      const currentStandards = getStandards();
      const newTertiaryStandards = await getTertiaryStandards(
        currentStandards,
        standardsList,
      );

      if (newTertiaryStandards.length === 0) {
        showAlert(
          "No new tertiary standards were found based on the current primary and secondary standards.",
        );
        return;
      }

      showAlert(
        `${newTertiaryStandards.length} tertiary standard${newTertiaryStandards.length !== 1 ? "s" : ""} will be added. Continue?`,
        () =>
          replace(
            [...currentStandards, ...newTertiaryStandards].sort(standardsSort),
          ),
      );
    } catch (error) {
      console.error("Error automating standards:", error);
      showAlert(
        "An error occurred while automating standards. Please try again.",
        null,
        "Error",
      );
    }
  };

  const tableProps = {
    columns: [
      {
        identifier: `order`,
        title: `Order`,
      },
      {
        identifier: `humanCodingScheme`,
        sx: { whiteSpace: "nowrap" },
        title: `Standard Name`,
      },
      {
        identifier: `fullStatement`,
        title: `Description`,
      },
    ],
    actions: [
      {
        disabled: readonly,
        label: `Remove Standard`,
        onClick: (row) => (e) => {
          setAlertDialogProps({
            confirmButtonText: "Remove",
            maxWidth: "xs",
            message: (
              <DialogContentText>
                {`The Standard "${row.vals.humanCodingScheme}" will be removed.  It can be added again by using the Add Standards button.`}
              </DialogContentText>
            ),
            onClose: (confirmed) => {
              setAlertDialogProps({ open: false });
              if (!confirmed) return;
              const standards = getStandards()
                .filter((_, idx) => idx !== row.idx)
                .sort(standardsSort);
              replace(standards);
            },
            open: true,
            dialogTitle: `Remove Standard`,
          });
        },
      },
      {
        disabled: readonly,
        label: `Edit Standard`,
        onClick: (row) => (e) => {
          const standards = getStandards();
          const selected = findStandard(row.vals.humanCodingScheme);
          setDialogProps({
            action: "edit",
            defaultValues: {
              documentSubject: selected.documentSubject,
              order: selected.order,
              standards: standards.map((s) => s.humanCodingScheme),
            },
            onClose: (data) => {
              if (data) {
                const updated = [
                  ...standards.filter(
                    (std) => std.humanCodingScheme !== data.humanCodingScheme,
                  ),
                  data,
                ].sort(standardsSort);
                replace(updated);
              }
              setDialogProps({ open: false });
            },
            open: true,
            selectedStandards: standards.map((s) => s.humanCodingScheme),
            selectedStandard: selected,
          });
        },
      },
    ],
    error: false,
    rows: standards,
  };

  return (
    <>
      {!readonly && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            marginBottom: 1,
            gap: 1,
          }}
        >
          {supportsAutomatedStandards(contentArea) && (
            <Button
              onClick={handleAutomateStandards}
              size="small"
              variant="outlined"
            >
              Automate Standards
            </Button>
          )}
          <Button
            onClick={() =>
              setDialogProps({
                action: "add",
                defaultValues: {
                  documentSubject: "",
                  order: "primary",
                  standards: [],
                },
                onClose: (data) => {
                  if (data)
                    replace(
                      [...getValues(`${objectType}.standards`), ...data].sort(
                        standardsSort,
                      ),
                    );
                  setDialogProps({ open: false });
                },
                open: true,
                selectedStandards: getValues(`${objectType}.standards`).map(
                  (s) => s.humanCodingScheme,
                ),
              })
            }
            size="small"
            startIcon={<PlusIcon />}
            variant="outlined"
          >
            Add Standards
          </Button>
        </Box>
      )}
      <SimpleTable {...tableProps} />
      {dialogProps.open && <StandardsDialog {...dialogProps} />}
      {alertDialogProps.open && <AlertDialog {...alertDialogProps} />}
    </>
  );
};

const standardsSort = (a, b) => {
  if (a.order.toUpperCase() < b.order.toUpperCase()) return -1;
  if (a.order.toUpperCase() > b.order.toUpperCase()) return 1;
  if (a.humanCodingScheme.toUpperCase() < b.humanCodingScheme.toUpperCase())
    return -1;
  if (a.humanCodingScheme.toUpperCase() > b.humanCodingScheme.toUpperCase())
    return 1;
  return 0;
};

StandardsTab.propTypes = {
  objectType: PropTypes.string.isRequired,
  readonly: PropTypes.bool.isRequired,
};

export { StandardsTab };
